/**
 * Session complete. The next session is usually already planned, so the button
 * does not wait.
 *
 * Tests come back through here too, after the grading job finishes — the
 * client polls rather than holding an HTTP request open on a connection that
 * may drop (docs/04).
 */

import { api, ApiError } from "../api.ts";
import { flushOutbox } from "../store.ts";
import type { DeferredAnswer } from "../blocks/types.ts";
import type { SubmitResponse } from "../types.ts";
import { el, mount, percent, primaryButton, shell, statTile, bar } from "../ui.ts";

export interface CompleteHandlers {
	openSession: (id: number) => void;
	backToToday: () => void;
}

export async function renderComplete(
	sessionId: number,
	deferred: DeferredAnswer[],
	handlers: CompleteHandlers,
): Promise<void> {
	renderWaiting("Submitting");

	// Answers first: the outbox must land before the server scores the session.
	await flushOutbox();

	let result: SubmitResponse;
	try {
		const response = await api.submit(sessionId, deferred);
		if ("grading_job_id" in response) {
			renderWaiting("Grading");
			const graded = await pollJob(response.grading_job_id);
			if (graded === null) {
				renderFailure("Grading did not finish. Your answers are saved — open the session again to retry.", handlers);
				return;
			}
			result = graded;
		} else {
			result = response;
		}
	} catch (error) {
		renderFailure(
			error instanceof ApiError ? error.message : "Submission needs a connection. Your answers are saved.",
			handlers,
		);
		return;
	}

	renderResult(sessionId, result, handlers);
}

function renderWaiting(label: string): void {
	mount(
		shell({
			eyebrowLeft: "Sessão",
			body: [
				el(
					"p",
					{ class: "en center", style: "margin-top:60px" },
					el("span", { class: "spinner", "aria-hidden": "true" }),
					` ${label}…`,
				),
			],
		}),
	);
}

function renderFailure(message: string, handlers: CompleteHandlers): void {
	mount(
		shell({
			eyebrowLeft: "Sessão",
			body: [el("div", { class: "notice notice--brick", text: message })],
			footer: primaryButton("Back to today", handlers.backToToday, { variant: "ghost" }),
		}),
	);
}

async function pollJob(jobId: string): Promise<SubmitResponse | null> {
	for (let attempt = 0; attempt < 60; attempt += 1) {
		await new Promise((resolve) => setTimeout(resolve, attempt < 5 ? 1_000 : 3_000));
		try {
			const job = await api.job(jobId);
			if (job.state === "succeeded" && job.result !== null) return job.result;
			if (job.state === "failed") return null;
		} catch {
			// Keep polling: a dropped request is not a failed job.
		}
	}
	return null;
}

function renderResult(sessionId: number, result: SubmitResponse, handlers: CompleteHandlers): void {
	const body: Node[] = [];

	body.push(
		el(
			"div",
			{ class: "grid-2" },
			statTile("Accuracy", percent(result.accuracy)),
			statTile("Flagged", String(result.flagged.length)),
		),
	);

	if (result.session_note.trim() !== "") {
		body.push(
			el(
				"div",
				{ class: "card", style: "margin-top:16px" },
				el("div", { class: "meta", style: "margin-bottom:7px", text: "Session note" }),
				el("p", { class: "en en--ink", lang: "en", text: result.session_note }),
			),
		);
	}

	if (result.flagged.length > 0) {
		const list = el("div", { class: "stack--tight" }, el("div", { class: "meta", style: "margin-bottom:8px", text: "Flagged for reinforcement" }));
		for (const item of result.flagged.slice(0, 8)) {
			list.append(el("span", { class: "chip chip--ochre", lang: "pt-BR", text: item.pt }));
		}
		body.push(list);
	}

	if (result.aptitude !== null && result.aptitude.by_concept.length > 0) {
		const aptitude = el("div", { class: "stack" }, el("div", { class: "meta", text: "Aptitude by concept" }));
		for (const entry of result.aptitude.by_concept) {
			aptitude.append(
				el(
					"div",
					{ style: "margin-top:13px" },
					el(
						"div",
						{ style: "display:flex;justify-content:space-between;align-items:baseline" },
						el("span", { class: "pt", style: "font-size:16px", lang: "pt-BR", text: entry.key }),
						el("span", { class: "en", style: "font-size:12px", text: percent(entry.accuracy) }),
					),
					bar(entry.accuracy, entry.accuracy >= 0.85 ? "var(--jade)" : entry.accuracy >= 0.7 ? "var(--azul)" : "var(--ochre)"),
				),
			);
		}
		body.push(aptitude);
	}

	const previousRatio = result.english_ratio_next;
	body.push(
		el(
			"div",
			{ class: "stack" },
			el("div", { class: "meta", text: `Immersion · ${percent(1 - previousRatio)} português` }),
			bar(1 - previousRatio),
		),
	);

	const footer = el("div");
	if (result.next_session_id !== null) {
		footer.append(
			el("p", { class: "en", style: "color:var(--jade);font-size:12.5px;margin-bottom:9px", text: "Next session planned and ready" }),
			primaryButton("Start the next session", () => handlers.openSession(result.next_session_id as number)),
		);
	} else if (result.next_session_state === "planning") {
		footer.append(
			el(
				"p",
				{ class: "en center", style: "margin-bottom:9px" },
				el("span", { class: "spinner", "aria-hidden": "true" }),
				" Preparing the next session",
			),
			primaryButton("Back to today", handlers.backToToday, { variant: "ghost" }),
		);
	} else {
		footer.append(primaryButton("Back to today", handlers.backToToday, { variant: "ghost" }));
	}

	mount(
		shell({
			eyebrowLeft: "Sessão concluída",
			eyebrowRight: result.degraded ? "review-only" : undefined,
			body,
			footer,
		}),
	);
}
